import { useCallback, useEffect, useRef } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Calculation, UpdateCalculationBody } from "../types/calculations";
import { useCalculationsApi } from "../useCalculations";
import { UNIT_SYSTEM_IMPERIAL, UNIT_SYSTEM_METRIC } from "../../types";
import { ConcreteResinCalculation, ConcreteResinFormValues } from "./types/calculations";

/**
 * Build form default values from a persisted calculation.
 * All fallback logic lives here — single source of truth for defaults.
 */
export function buildResinFormDefaults(calculation?: Calculation<ConcreteResinCalculation>): ConcreteResinFormValues {
  const d = calculation?.data;
  return {
    unitSystem: d?.unitSystem === UNIT_SYSTEM_IMPERIAL ? UNIT_SYSTEM_IMPERIAL : UNIT_SYSTEM_METRIC,
    chemicalAnchorId: d?.chemicalAnchorId ?? null,
    rodDiameter: d?.rodDiameter ?? null,
    holeDiameter: d?.holeDiameter ?? null,
    holeDepth: d?.holeDepth ?? null,
    holeNumbers: d?.numHoles ?? null,
    overageFactor: d?.overageFactor ?? 10.0,
  };
}

// The calculation's inputs as the backend takes them.
const toCalculationData = (values: Partial<ConcreteResinFormValues>) => ({
  unitSystem: values.unitSystem ?? null,
  chemicalAnchorId: values.chemicalAnchorId ?? null,
  rodDiameter: values.rodDiameter ?? null,
  holeDiameter: values.holeDiameter ?? null,
  holeDepth: values.holeDepth ?? null,
  numHoles: values.holeNumbers ?? null,
  overageFactor: values.overageFactor ?? null,
});

const serializeForComparison = (values: Partial<ConcreteResinFormValues>): string =>
  JSON.stringify(toCalculationData(values));

const AUTO_SAVE_DELAY_MS = 350;

export type AutoSaveResinReturn = {
  /** Force an immediate autosave — use after programmatic form.reset() to trigger computation */
  triggerSave: () => void;
  /** Fires the debounced save at once, if any (or again a failed one), and waits for it: the server has the form's values */
  waitForSave: () => Promise<void>;
};

/**
 * Saves the form's values 350 ms after their last change. react-hook-form is the single source of
 * truth for input values; the backend for the computed ones, which come back with the calculation
 * itself (its cache gets the save's response).
 *
 * Neither a change nor a save re-renders the page: the values come from a form subscription, outside
 * render, and no saving state is kept - only a failure is surfaced, through onError.
 */
export function useAutoSaveResinCalculation(
  calculation: Calculation<ConcreteResinCalculation> | undefined,
  formMethods: Pick<UseFormReturn<ConcreteResinFormValues>, "getValues" | "subscribe">,
  isAdmin?: boolean,
  onError?: (error: unknown) => void,
): AutoSaveResinReturn {
  const { getValues, subscribe } = formMethods;
  const { updateCalculation } = useCalculationsApi();
  // The latest of each, for the subscription below, which outlives the render it was set up in: it's
  // set up once, as re-subscribing would drop the pending save (updateCalculation is a new function on
  // every render, and a save's response re-renders the page).
  const calculationRef = useRef(calculation);
  const isAdminRef = useRef(isAdmin);
  const onErrorRef = useRef(onError);
  const updateCalculationRef = useRef(updateCalculation);
  // The values last saved, or loaded: an equal change isn't saved. Taken from the calculation once, not
  // from every save's response (see useAutoSaveDeckDesignerConfiguration): the page is mounted again
  // for another calculation.
  const lastSerializedRef = useRef(calculation ? serializeForComparison(buildResinFormDefaults(calculation)) : "");
  // The debounced save not fired yet, and the last one fired: what waitForSave flushes and awaits.
  const pendingSaveRef = useRef<{ timer: number; save: () => void } | null>(null);
  const lastSaveRef = useRef<Promise<unknown>>(Promise.resolve());
  const hasLastSaveFailedRef = useRef(false);

  useEffect(() => {
    calculationRef.current = calculation;
    isAdminRef.current = isAdmin;
    onErrorRef.current = onError;
    updateCalculationRef.current = updateCalculation;
  }, [calculation, isAdmin, onError, updateCalculation]);

  const cancelPendingSave = useCallback(() => {
    if (pendingSaveRef.current) {
      window.clearTimeout(pendingSaveRef.current.timer);
      pendingSaveRef.current = null;
    }
  }, []);

  // Restarts the debounce for these values: a newer change cancels the pending save, which then goes
  // out only if they differ from the last saved ones.
  const scheduleSave = useCallback(
    (values: Partial<ConcreteResinFormValues>) => {
      cancelPendingSave();

      const currentCalculation = calculationRef.current;
      if (!currentCalculation?.canEdit) return;

      const nextSerialized = serializeForComparison(values);
      if (nextSerialized === lastSerializedRef.current) return;

      const save = () => {
        pendingSaveRef.current = null;
        lastSerializedRef.current = nextSerialized;
        const data = toCalculationData(values);

        const body: UpdateCalculationBody<ConcreteResinCalculation> = {
          name: currentCalculation.name,
          description: currentCalculation.description ?? null,
          data,
        };
        const request = updateCalculationRef.current(
          currentCalculation.project?.id,
          currentCalculation.id,
          body,
          {
            ...currentCalculation,
            data: {
              ...currentCalculation.data,
              ...data,
              calculationId: currentCalculation.id,
              unitSystem: data.unitSystem ?? currentCalculation.data?.unitSystem ?? null,
            },
          },
          isAdminRef.current,
        );
        lastSaveRef.current = request;
        request.then(
          () => (hasLastSaveFailedRef.current = false),
          (err) => {
            hasLastSaveFailedRef.current = true;
            onErrorRef.current?.(err);
          },
        );
      };
      pendingSaveRef.current = { timer: window.setTimeout(save, AUTO_SAVE_DELAY_MS), save };
    },
    [cancelPendingSave],
  );

  useEffect(() => {
    const unsubscribe = subscribe({
      formState: { values: true },
      callback: ({ values }) => scheduleSave(values),
    });

    return () => {
      unsubscribe();
      cancelPendingSave();
    };
  }, [cancelPendingSave, scheduleSave, subscribe]);

  const triggerSave = useCallback(() => {
    lastSerializedRef.current = "";
    scheduleSave(getValues());
  }, [getValues, scheduleSave]);

  // A failed last save is tried again: rejects, once reported through onError, if it fails again.
  const waitForSave = useCallback(async () => {
    if (!pendingSaveRef.current && hasLastSaveFailedRef.current) {
      lastSerializedRef.current = "";
      scheduleSave(getValues());
    }
    const pendingSave = pendingSaveRef.current;
    if (pendingSave) {
      window.clearTimeout(pendingSave.timer);
      pendingSave.save();
    }
    await lastSaveRef.current;
  }, [getValues, scheduleSave]);

  return { triggerSave, waitForSave };
}
